/**
 * /policies/payroll/earnings-catalog — Session 3c PR 14.
 * Owning agent: Rawatib.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { EarningCodeItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function PayrollEarningsCatalogPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-payroll-earnings", le],
    queryFn: () => session3cApi.listEarnings(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<EarningCodeItem>[] = [
    {
      headerKey: "policies.payroll_earnings.col.code",
      label: "Code",
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    {
      headerKey: "policies.payroll_earnings.col.name",
      label: "Name",
      render: (r) => <span className="font-medium">{r.name_en}</span>,
    },
    {
      headerKey: "policies.payroll_earnings.col.category",
      label: "Category",
      render: (r) => <span className="text-xs text-text-secondary">{r.category}</span>,
    },
    {
      headerKey: "policies.payroll_earnings.col.calc",
      label: "Calc",
      render: (r) => <span className="text-xs">{r.calc_behavior}</span>,
    },
    {
      headerKey: "policies.payroll_earnings.col.flags",
      label: "Flags",
      render: (r) => (
        <div className="flex gap-1 text-[10px]">
          {r.taxable && <span className="rounded bg-rose-100 px-1.5 text-rose-800">tax</span>}
          {r.gosi_inclusive && <span className="rounded bg-amber-100 px-1.5 text-amber-800">gosi</span>}
          {r.eos_inclusive && <span className="rounded bg-emerald-100 px-1.5 text-emerald-800">eos</span>}
        </div>
      ),
    },
  ];

  return (
    <PolicyListPage<EarningCodeItem>
      title={t("policies.payroll_earnings.title", "Earnings Catalog")}
      subtitle={t("policies.payroll_earnings.subtitle", "All earnings codes — basic salary · allowances · bonuses · commissions · OT · ad-hoc.")}
      agent="rawatib"
      domain="payroll_earnings"
      newButtonLabel={t("policies.payroll_earnings.cta.new", "New Earning Code")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.payroll_earnings.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.payroll_earnings.suggestion.clean_body", "Each code carries Taxable + GOSI-Inclusive + EOS-Inclusive flags driving the M3X.PAYROLL calc engine."),
          primaryActionLabel: t("policies.payroll_earnings.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "payroll-earnings-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.payroll_earnings.title", "Earnings Catalog"),
        window_label: t("policies.payroll_earnings.window", "active codes"),
      }}
    />
  );
}
